import { memo } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import useSWRMutation from 'swr/mutation';

import { VideoCard } from './videoCard/VideoCard';

import css from './DraggableVideoCards.module.css';

import { videoService } from 'v2/api/videoService';
import { SortableCard } from 'v2/components/card/sortableCard/SortableCard';
import { useSortableList } from 'v2/hooks/useSortableList';
import { VideoModel } from 'v2/types/VideoModel';

interface DraggableVideoCardsProps {
  videos: VideoModel[];
  onRevalidate: VoidFunction;
}

export const DraggableVideoCards = memo<DraggableVideoCardsProps>(({ videos, onRevalidate }) => {
  const { items, itemIds, sensors, activeItem, onDragStart, onDragEnd, onDragCancel } = useSortableList<VideoModel>(
    videos,
    {
      onReorder: (next) => {
        trigger(next.map((item, i) => ({ id: item.id, order: i + 1 })));
      },
    }
  );

  const { trigger } = useSWRMutation('changeVideoOrder', videoService.changeVideoOrder, {
    onSuccess: () => {
      onRevalidate();
    },
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={itemIds}>
        <div className={css.draggableVideoCards}>
          {items.map((item) => (
            <SortableCard key={item.id} id={item.id}>
              <VideoCard video={item} onRevalidate={onRevalidate} />
            </SortableCard>
          ))}
        </div>
      </SortableContext>

      <DragOverlay adjustScale style={{ cursor: 'grabbing' }}>
        {activeItem && <VideoCard video={activeItem} onRevalidate={onRevalidate} />}
      </DragOverlay>
    </DndContext>
  );
});

DraggableVideoCards.displayName = 'DraggableVideoCards';
